import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import moment from "moment";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, snackbarData, userCredentials } from "recoilState/GlobalState";
import { useService } from "components/route/ServiceRoute";

import { DataGridPro, esES, LicenseInfo } from "@mui/x-data-grid-pro";
// COPIAR (desconectado): GRID_CHECKBOX_SELECTION_COL_DEF se usaba para pinear la columna
// de checkboxes junto a "empleado".
import { LICENSE_KEY } from "consts/env";
import { IconButton, Tooltip } from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ZoomInRounded from "@mui/icons-material/ZoomInRounded";

import { Button, HeaderButton } from "components";
import { Tab, Tabs } from "@mui/material";
// COPIAR (desconectado): import { Menu, MenuItem } from "@mui/material";
import Filters from "components/Filters";
import Modal from "components/UI/Modal/Modal";
import { RNHEstablecimientoGet } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoGet";

import s from "styles/pages/Pages.module.scss";
import gridClasses from "components/UI/Grid/XGrid.module.scss";
import styles from "./ControlHorarios.module.scss";

import { CELDA_VACIA, TURNOS, TURNOS_EXTRA, esDiaTrabajado } from "./consts";
import {
  empleadosHorariosGet,
  empleadosHorariosSet,
  empleadosTExGet,
  empleadosTExSet,
} from "./mock/empleadosHorarios.mock";
import { getMonthStart, formatMonthLabel, monthKey, getDiasDelMes, formatDiaLabel, esCampoDia } from "./utils/fechas";
import { useFeriadosDelMes } from "./hooks/useFeriadosDelMes";
import { useHorariosDraft } from "./hooks/useHorariosDraft";
import { useProteccionSalida } from "./hooks/useProteccionSalida";
import HorarioCellEditor from "./components/HorarioCellEditor";
import DetalleCeldaModal from "./components/DetalleCeldaModal";
import ResumenEmpleadoModal from "./components/ResumenEmpleadoModal";
// COPIAR (desconectado): import { filasATSV, copiarAlPortapapeles } from "./utils/portapapeles";

LicenseInfo.setLicenseKey(LICENSE_KEY);

const TOTAL_ROW_ID = "__totales__";

/**
 * Las dos vistas del mismo mes y los mismos empleados.
 *
 * Comparten hospital, mes, la lista de empleados y la barra de botones: lo unico que cambia al
 * pasar de una a la otra es que se carga en la celda.
 *
 * TEx son GUARDIAS: la mayoria de los dias no tiene nada cargado, y por eso ahi la celda
 * vacia va en blanco — en Turnos un dia vacio es trabajo pendiente y se marca con "—", aca es
 * lo normal y marcarlo seria ruido que tapa las guardias.
 */
const SOLAPAS = [
  { key: "turnos", label: "Turnos" },
  { key: "tex", label: "Turnos Extraordinarios" },
];
// Altura fija de fila: la celda muestra un único código de turno (M/T/N) o "—".
const ALTO_FILA = 44;

// Un día en el que la persona TRABAJA: franco y licencia no cuentan, aunque sean códigos
// cargados. Es lo que mide la fila de totales ("Agentes por día").
const trabaja = (celda) => esDiaTrabajado(celda?.codigo);

// Campos de la row que no son celdas-día.
const NO_ES_DIA = ["id", "empleado", "francos"];

/**
 * Cuántos días de cada código tiene la fila: `{ M: 8, T: 5, N: 4, F: 12, L: 2 }`.
 *
 * Es la base de las dos cosas que se muestran: la columna final toma `F` —que es lo único que
 * pide la planilla— y el modal de la lupa muestra el desglose entero. Las horas salen de acá
 * también, multiplicando por la duración de cada turno, así que no hace falta recorrer dos
 * veces la fila.
 */
const contarPorCodigo = (row) =>
  Object.keys(row)
    .filter((key) => !NO_ES_DIA.includes(key))
    .reduce((acc, key) => {
      const codigo = row[key]?.codigo;
      if (codigo) acc[codigo] = (acc[codigo] ?? 0) + 1;
      return acc;
    }, {});

// Fila de totales: cantidad de empleados que trabajan cada día + francos del mes.
const construirFilaTotales = (filas, dias) => {
  const total = { id: TOTAL_ROW_ID, empleado: "Agentes por día" };
  dias.forEach(({ field }) => {
    total[field] = filas.filter((row) => trabaja(row[field])).length;
  });
  total.francos = filas.reduce((acc, row) => acc + (contarPorCodigo(row).F ?? 0), 0);
  return total;
};


/**
 * Como se marca una columna-dia que cae en feriado, o null si no lo es. Una sola funcion
 * para el encabezado, la celda y la leyenda, para que no se desincronicen.
 *
 * Provincial solo si el tipo es exactamente "P": un valor inesperado cae en nacional y
 * asi se VE, en vez de desaparecer de la grilla.
 */
function marcaDeFeriado(feriados, field) {
  const feriado = feriados?.[field];
  if (!feriado) return null;

  return {
    clase:
      feriado.tipo === "P" ? styles.feriadoProvincial : styles.feriadoNacional,
    // Los dos nombres cuando coinciden dos feriados el mismo dia. El tinte muestra uno
    // solo —gana el nacional—, asi que el tooltip es el unico lugar donde se ven ambos.
    titulo: feriado.nombres.join(" \u00b7 "),
  };
}

function renderHorarioCell(params) {
  if (params.row.id === TOTAL_ROW_ID) {
    return <span>{params.value}</span>;
  }
  const celda = params.value ?? CELDA_VACIA;
  // Solo la letra, en negro: la grilla va sin color. El guion es un dia sin cargar.
  const contenido = celda.codigo ? (
    <span className={styles.codigoTurno}>{celda.codigo}</span>
  ) : (
    <span className={styles.franco}>—</span>
  );

  // Envoltorio con posición relativa para anclar el puntito de observación.
  const cuerpo = (
    <div className={styles.celdaIndicadores}>
      {contenido}
      {celda.observacion && <span className={styles.puntoObservacion} />}
    </div>
  );

  if (!celda.observacion) return cuerpo;
  return (
    <Tooltip title={celda.observacion} arrow>
      {cuerpo}
    </Tooltip>
  );
}

/**
 * Celda de TEx. Vacia va EN BLANCO, sin el "—" de Turnos.
 *
 * Son guardias: lo normal es que un dia no tenga nada. Marcar cada vacio taparia justo lo que
 * importa, que son los pocos dias que si tienen algo.
 */
function renderTExCell(params) {
  const celda = params.value ?? CELDA_VACIA;
  if (!celda.codigo) return null;

  const cuerpo = (
    <div className={styles.celdaIndicadores}>
      <span className={styles.codigoTurno}>{celda.codigo}</span>
      {celda.observacion && <span className={styles.puntoObservacion} />}
    </div>
  );

  if (!celda.observacion) return cuerpo;
  return (
    <Tooltip title={celda.observacion} arrow>
      {cuerpo}
    </Tooltip>
  );
}

/**
 * Cuantas guardias tiene la fila.
 *
 * Cada dia cargado vale UNA, sin importar el codigo: una `M/T` es una guardia igual que una
 * `M`. Por eso no hay desglose ni lupa en esta solapa — el numero ya lo dice todo.
 */
const contarGuardias = (row) =>
  Object.keys(row).filter((key) => esCampoDia(key) && row[key]?.codigo).length;

export default function ControlHorarios() {
  const setRoute = useSetRecoilState(routeInfo);
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
// DESESTRUCTURADO: `useService()` devuelve el contexto del Outlet, que es `{ service }`.
  // Sin las llaves, cualquier campo que se le pida da undefined.
  const { service } = useService();

  const [hospitales, setHospitales] = useState([]);
  const [monthStart, setMonthStart] = useState(() => getMonthStart(moment()));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmacion, setConfirmacion] = useState({ isOpen: false, onConfirm: null });
  const [loadedHospitalId, setLoadedHospitalId] = useState("");
  // Selección única de celda-día (click simple); habilita la lupa de detalle.
  const [celdaSeleccionada, setCeldaSeleccionada] = useState(null);
  /* COPIAR (desconectado) — ver nota al pie del archivo.
  const [filasTildadas, setFilasTildadas] = useState([]);
  const anclaCopiar = useRef(null);
  const [menuCopiarAbierto, setMenuCopiarAbierto] = useState(false);
  */
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [solapa, setSolapa] = useState(SOLAPAS[0].key);
  // Las referencias arrancan PLEGADAS: son material de consulta, no algo que haga falta a la
  // vista mientras se carga el mes.
  const [referenciasAbiertas, setReferenciasAbiertas] = useState(false);
  const enTurnos = solapa === "turnos";
  // Fila cuyo desglose se está mirando con la lupa. `null` = modal cerrado.
  const [resumenDe, setResumenDe] = useState(null);

  const {
    rows,
    cambiosPendientes,
    getCambiosPendientes,
    processRowUpdate,
    hayCambios,
    loadRows,
    marcarGuardado,
    descartarCambios,
  } = useHorariosDraft([]);

  // Una instancia del hook por solapa: cada una conserva sus cambios al cambiar de
  // solapa. Sin desestructurar, para no renombrar los veinte usos del de turnos.
  const tex = useHorariosDraft([]);

  // Pendientes de LAS DOS. De aca sale el contador del boton, si se puede guardar, y el aviso
  // al salir: nadie se va con trabajo sin guardar en la solapa que no estaba mirando.
  const hayCambiosTotal = hayCambios || tex.hayCambios;
  const cantidadCambiosTotal =
    Object.keys(cambiosPendientes).length + Object.keys(tex.cambiosPendientes).length;

  const formik = useFormik({
    initialValues: { hospitalId: "" },
    onSubmit: (values) => {
      // Ya está cargado ese hospital: filtrar de nuevo no aporta nada
      // (y con cambios pendientes dispararía la advertencia al pedo).
      if (String(values.hospitalId) === String(loadedHospitalId)) return;
      intentarNavegar(() => cargarHorarios(values.hospitalId, monthStart));
    },
  });

  // Los feriados del mes. No entra en `cargarHorarios` porque dependen del MES y no del
  // hospital: ver la nota en el propio hook.
  const { feriados } = useFeriadosDelMes(monthStart);

  const cargarHorarios = async (hospitalId, ms) => {
    if (!hospitalId) return;
    setIsLoading(true);
    setCeldaSeleccionada(null);
    // Las dos solapas se cargan juntas: comparten hospital y mes, y cambiar de solapa no
    // tiene que disparar una espera.
    const [result, resultTEx] = await Promise.all([
      empleadosHorariosGet({ hospitalId, mes: monthKey(ms) }),
      empleadosTExGet({ hospitalId, mes: monthKey(ms) }),
    ]);
    loadRows(result);
    tex.loadRows(resultTEx);
    setLoadedHospitalId(hospitalId);
    setIsLoading(false);
  };

  // Click simple selecciona la celda-día (única); re-click la mantiene para no
  // interferir con el doble click de edición. Clicks fuera de celdas de día deseleccionan.
  const handleCellClick = (params) => {
    // Por inclusion: antes era "todo lo que no sea empleado ni guardias", y al sumar la
    // columna de checkboxes esa pasaba el filtro. Se guardaba como celda seleccionada y el
    // modal de detalle intentaba formatear "__check__" como fecha.
    if (!esCampoDia(params.field) || params.id === TOTAL_ROW_ID) {
      setCeldaSeleccionada(null);
      return;
    }
    setCeldaSeleccionada({ id: params.id, field: params.field });
  };

  const filaSeleccionada = celdaSeleccionada ? rows.find((row) => row.id === celdaSeleccionada.id) : null;
  const celdaDetalle = filaSeleccionada ? (filaSeleccionada[celdaSeleccionada.field] ?? CELDA_VACIA) : CELDA_VACIA;

  // El guardado del modal solo registra la observación en el draft (mismo lote,
  // mismo contador N que los horarios); la persistencia real es el Guardar general.
  const guardarObservacion = (texto) => {
    if (filaSeleccionada && celdaSeleccionada) {
      const celdaActual = filaSeleccionada[celdaSeleccionada.field] ?? CELDA_VACIA;
      if ((celdaActual.observacion ?? "") !== texto) {
        processRowUpdate({
          ...filaSeleccionada,
          [celdaSeleccionada.field]: { ...celdaActual, observacion: texto },
        });
      }
    }
    setDetalleAbierto(false);
  };

  const guardarCambios = async () => {
    // Lectura sincrónica: incluye la celda confirmada por blur en este mismo click.
    const cambios = getCambiosPendientes();
    const cambiosTEx = tex.getCambiosPendientes();
    const hayAlgo = Object.keys(cambios).length || Object.keys(cambiosTEx).length;
    if (!hayAlgo || isSaving) return true;
    setIsSaving(true);
    try {
      // Las dos solapas en el mismo guardado: si solo se guardara la visible, se podrian
      // dejar cambios colgados en la otra sin enterarse. Son dos servicios distintos, asi
      // que van en paralelo y cualquiera que falle corta el guardado entero.
      await Promise.all([
        Object.keys(cambios).length ? empleadosHorariosSet({ cambios }) : null,
        Object.keys(cambiosTEx).length ? empleadosTExSet({ cambios: cambiosTEx }) : null,
      ]);
      marcarGuardado();
      tex.marcarGuardado();
      setSnackBarInfo({
        message: "Horarios guardados correctamente",
        severity: "success",
        open: true,
      });
      return true;
    } catch (error) {
      console.error("Error al guardar horarios:", error);
      setSnackBarInfo({
        message: "No se pudieron guardar los cambios",
        severity: "error",
        open: true,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDescartar = () => {
    // Las dos, por lo mismo que el guardado: descartar solo la visible dejaria pendientes
    // invisibles y el aviso al salir seguiria apareciendo sin motivo aparente.
    descartarCambios();
    tex.descartarCambios();
    setSnackBarInfo({
      message: "Cambios descartados",
      severity: "success",
      open: true,
    });
  };

  // Si hay cambios sin guardar, pide confirmación antes de ejecutar la acción de navegación.
  const intentarNavegar = (accion) => {
    if (!hayCambiosTotal) {
      accion();
      return;
    }
    setConfirmacion({ isOpen: true, onConfirm: accion });
  };

  // Misma confirmación, invocada desde la protección de salida de página.
  const solicitarConfirmacion = useCallback((continuar) => {
    setConfirmacion({ isOpen: true, onConfirm: continuar });
  }, []);

  useProteccionSalida(hayCambiosTotal, solicitarConfirmacion);

  const cancelarConfirmacion = () => {
    // Revierte el select por si el cambio pendiente vino de elegir otro hospital.
    formik.setFieldValue("hospitalId", loadedHospitalId);
    setConfirmacion({ isOpen: false, onConfirm: null });
  };

  const descartarYContinuar = () => {
    const accion = confirmacion.onConfirm;
    setConfirmacion({ isOpen: false, onConfirm: null });
    descartarCambios();
    if (accion) accion();
  };

  const guardarYContinuar = async () => {
    const accion = confirmacion.onConfirm;
    const ok = await guardarCambios();
    if (!ok) return; // el guardado falló: se mantienen el modal y los cambios
    setConfirmacion({ isOpen: false, onConfirm: null });
    if (accion) accion();
  };

  const navegarMes = (delta) => {
    intentarNavegar(() => {
      const nuevoMes = getMonthStart(moment(monthStart).add(delta, "month"));
      setMonthStart(nuevoMes);
      cargarHorarios(formik.values.hospitalId, nuevoMes);
    });
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      // Solo hospitales: sin el tipo, el Get devuelve todos los componentes
      // (CAPS, puestos, postas y URESA comparten tabla) y el select los mezcla.
      const { data: result } = await RNHEstablecimientoGet(credentials, { tipo: "H" });
      const lista = result?.[0]?.rnhEstablecimientoId ? result : [];
      setHospitales(lista);
      if (lista[0]) {
        const primerId = String(lista[0]?.rnhEstablecimientoId);
        formik.setFieldValue("hospitalId", primerId);
        await cargarHorarios(primerId, monthStart);
      } else {
        // Sin hospitales (o service caído): mostrar igual el mock del mes actual.
        const result = await empleadosHorariosGet({ hospitalId: null, mes: monthKey(monthStart) });
        loadRows(result);
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    setRoute({
      wordKey: `Control de Horarios - Programa Salud RN`
    })
  }, [service.servicioNombre, setRoute])

  /* COPIAR (desconectado)

  Copia filas como TSV, listo para pegar en Excel: las columnas VISIBLES en su orden visible
  —empleado, los dias del mes y horas—, sin encabezado de fechas, y nunca la fila de totales
  (vive en `pinnedRows`, no en `rows`).

  const copiar = async (filas, descripcion) => {
    setMenuCopiarAbierto(false);
    if (!filas.length) return;

    const ok = await copiarAlPortapapeles(filasATSV(filas, columns));
    setSnackBarInfo({
      message: ok ? `${descripcion} al portapapeles` : "No se pudo copiar al portapapeles",
      severity: ok ? "success" : "error",
      open: true,
    });
  };

  const filaDeLaCelda = rows.find((row) => row.id === celdaSeleccionada?.id);
  const tildadas = rows.filter((row) => filasTildadas.includes(row.id));
  */

  // Misma estructura que Turnos y el mismo `getDiasDelMes`, para que cambiar de solapa
  // no mueva ninguna columna de lugar.
  const columnsTEx = useMemo(() => [
    { field: "empleado", headerName: "Agente", width: 180, editable: false, sortable: false },
    ...getDiasDelMes(monthStart).map(({ field, label }) => {
      const feriado = marcaDeFeriado(feriados, field);
      return {
        field,
        headerName: label,
        headerAlign: "center",
        align: "center",
        sortable: false,
        // Angosta a proposito: la celda muestra un codigo de una o tres letras y el header
        // dos mas el numero. Con 120 entraba la mitad de los dias en pantalla.
        width: 64,
        editable: true,
        renderCell: renderTExCell,
        // El mismo editor que Turnos, con la otra lista: seis opciones, sin franco ni licencia.
        renderEditCell: (params) => (
          <HorarioCellEditor {...params} opciones={TURNOS_EXTRA} />
        ),
        headerClassName: feriado?.clase,
        // Sin feriado va `undefined` y manda `headerName`, que es el camino normal.
        renderHeader: feriado
          ? () => (
            <Tooltip title={feriado.titulo} arrow>
              <span>{label}</span>
            </Tooltip>
          )
          : undefined,
      };
    }),
    {
      field: "guardias",
      headerName: "Guardias",
      width: 100,
      sortable: false,
      editable: false,
      headerAlign: "center",
      align: "center",
      cellClassName: styles.francosCell,
      // Sin lupa: cada guardia vale lo mismo, asi que el numero no tiene desglose detras.
      valueGetter: (params) => contarGuardias(params.row),
    },
  ], [monthStart, feriados]);

  const columns = useMemo(() => [
    { field: "empleado", headerName: "Agente", width: 180, editable: false, sortable: false },
    ...getDiasDelMes(monthStart).map(({ field, label }) => {
      const feriado = marcaDeFeriado(feriados, field);
      return {
        field,
        headerName: label,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        width: 64,
        editable: true,
        renderCell: renderHorarioCell,
        renderEditCell: (params) => <HorarioCellEditor {...params} />,
        headerClassName: feriado?.clase,
        renderHeader: feriado
          ? () => (
            <Tooltip title={feriado.titulo} arrow>
              <span>{label}</span>
            </Tooltip>
          )
          : undefined,
      };
    }),
    {
      field: "francos",
      headerName: "Francos",
      width: 100,
      sortable: false,
      editable: false,
      headerAlign: "center",
      align: "center",
      cellClassName: styles.francosCell,
      valueGetter: (params) => {
        if (params.row.id === TOTAL_ROW_ID) return params.row.francos;
        return contarPorCodigo(params.row).F ?? 0;
      },
      // El número es lo que pide la planilla; el resto del desglose entra por la lupa, que
      // evita sumar cinco columnas fijas y comerse días visibles.
      renderCell: (params) =>
        params.row.id === TOTAL_ROW_ID ? (
          <span>{params.value}</span>
        ) : (
          <span className={styles.francosConLupa}>
            {params.value}
            <button
              type="button"
              className={styles.lupaResumen}
              title={`Ver el detalle de ${params.row.empleado}`}
              onClick={(e) => {
                // Sin esto el click también selecciona la celda y mueve la lupa de detalle.
                e.stopPropagation();
                setResumenDe(params.row);
              }}
            >
              <ZoomInRounded fontSize="small" />
            </button>
          </span>
        ),
    },
  ], [monthStart, feriados]);

  // Fila de totales derivada de las rows reales, pinneada al fondo de la grilla:
  // queda siempre visible al hacer scroll interno (sin autoHeight el row-pinning
  // funciona bien). Con la grilla de alto fijo, ya no hacen falta filas de relleno.
  const pinnedRows = useMemo(
    () => ({
      bottom: rows.length ? [construirFilaTotales(rows, getDiasDelMes(monthStart))] : [],
    }),
    [rows, monthStart]
  );



  // El ítem de la leyenda del puntito naranja solo aparece si hay al menos una
  // observación cargada en los datos actuales.
  const hayFeriados = useMemo(() => Object.keys(feriados).length > 0, [feriados]);

  const hayObservaciones = useMemo(
    () =>
      rows.some((row) =>
        Object.keys(row).some(
          (key) => key !== "id" && key !== "empleado" && row[key]?.observacion
        )
      ),
    [rows]
  );

  return (
    <main className={s.main}>
      <Modal open={confirmacion.isOpen} title="Cambios sin guardar" onClose={cancelarConfirmacion} narrow>
        <p>Tenés cambios sin guardar. ¿Qué querés hacer?</p>
        <div className={styles.confirmButtons}>
          <Button disabled={isSaving} onClick={(e) => { e.preventDefault(); guardarYContinuar(); }}>
            <span>Guardar y continuar</span>
          </Button>
          <Button disabled={isSaving} onClick={(e) => { e.preventDefault(); descartarYContinuar(); }}>
            <span>Descartar y continuar</span>
          </Button>
          <Button disabled={isSaving} onClick={(e) => { e.preventDefault(); cancelarConfirmacion(); }}>
            <span>Cancelar</span>
          </Button>
        </div>
      </Modal>

      <ResumenEmpleadoModal
        open={Boolean(resumenDe)}
        empleado={resumenDe?.empleado}
        mesLabel={formatMonthLabel(monthStart)}
        conteo={resumenDe ? contarPorCodigo(resumenDe) : {}}
        diasDelMes={getDiasDelMes(monthStart).length}
        onClose={() => setResumenDe(null)}
      />

      <DetalleCeldaModal
        open={detalleAbierto}
        empleado={filaSeleccionada?.empleado ?? ""}
        fechaLabel={celdaSeleccionada ? formatDiaLabel(celdaSeleccionada.field) : ""}
        celda={celdaDetalle}
        onGuardar={guardarObservacion}
        onClose={() => setDetalleAbierto(false)}
      />

      <div className={s["filters-actions"]}>
        <div className={styles.filtrosConLeyenda}>
          <Filters
            formik={formik}
            inputsRender={
              [
                // `width` lo toma FormikSelectInput como `anchoInput`. Los 138px por defecto
                // de los filtros compartidos cortan nombres como "Hospital Artémides Zatti";
                // 50% mas alcanza sin desbalancear la barra.
                { type: "select", name: "hospitalId", label: "Hospital", disabled: false, options: hospitales, firtsOptionKey: "rnhEstablecimientoId", firstOptionName: "rnhEstablecimientoNombre", firstOptionValue: "rnhEstablecimientoId", all: false, width: "270px" },
              ]
            }
            disabled={isLoading}
          />
        </div>
        <div className={styles.toolbarDerecha}>
          <div className={styles.weekNav}>
            <IconButton
              aria-label="Mes anterior"
              onClick={() => navegarMes(-1)}
              disabled={isLoading || !formik.values.hospitalId}
            >
              <ChevronLeft />
            </IconButton>
            <span className={styles.weekLabel}>
              {formatMonthLabel(monthStart)}
            </span>
            <IconButton
              aria-label="Mes siguiente"
              onClick={() => navegarMes(1)}
              disabled={isLoading || !formik.values.hospitalId}
            >
              <ChevronRight />
            </IconButton>
          </div>
          <div className={styles.actionButtons}>
            <HeaderButton
              text={"Ver detalle"}
              onClick={() => setDetalleAbierto(true)}
              disabled={!celdaSeleccionada || isLoading}
            >
              <SearchOutlined />
            </HeaderButton>
            {/* COPIAR (desconectado)
            <span ref={anclaCopiar}>
              <HeaderButton
                text={"Copiar"}
                onClick={() => setMenuCopiarAbierto(true)}
                disabled={isLoading || !rows.length}
              >
                <ContentCopyRounded />
              </HeaderButton>
            </span>
            <Menu
              anchorEl={anclaCopiar.current}
              open={menuCopiarAbierto}
              onClose={() => setMenuCopiarAbierto(false)}
            >
              <MenuItem disabled={!filaDeLaCelda} onClick={() => copiar([filaDeLaCelda], `Fila de ${filaDeLaCelda?.empleado} copiada`)}>
                Esta fila
              </MenuItem>
              <MenuItem disabled={!tildadas.length} onClick={() => copiar(tildadas, `${tildadas.length} filas copiadas`)}>
                {`Tildadas (${tildadas.length})`}
              </MenuItem>
              <MenuItem onClick={() => copiar(rows, `${rows.length} filas copiadas`)}>
                Todo el mes
              </MenuItem>
            </Menu>
            */}
            <Button
              disabled={!hayCambiosTotal || isSaving || isLoading}
              onClick={handleDescartar}
            >
              <span>Descartar cambios</span>
            </Button>
            <Button
              disabled={!hayCambiosTotal || isSaving || isLoading}
              onClick={() => guardarCambios()}
            >
              <span>{`Guardar cambios${hayCambiosTotal ? ` (${cantidadCambiosTotal})` : ""}`}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Entre la barra y la grilla: la solapa queda pegada a la tabla que cambia, y lo de
          arriba —hospital, mes, guardar— se lee como que vale para las dos. */}
      <div className={styles.solapasConLeyenda}>
        <Tabs
          className={styles.solapas}
          value={solapa}
          onChange={(_e, nueva) => {
            setSolapa(nueva);
            // La celda seleccionada es de la solapa que se deja: `getCellClassName` la
            // resalta por id + field, y esa combinación existe en las dos tablas, así que
            // sin limpiarla queda marcada una celda de la otra. Además habilitaba
            // "Ver detalle" sobre un día que ya no se está mirando.
            setCeldaSeleccionada(null);
          }}
        >
          {SOLAPAS.map(({ key, label }) => (
            <Tab key={key} value={key} label={label} disableRipple />
          ))}
        </Tabs>

        {/* El puntito de observación va con las solapas y no con los códigos: no es un
            valor que se elija en la celda, es una marca que la celda puede tener además de
            su valor. Mezclarlo con los códigos lo hacía parecer uno más. */}
        {hayObservaciones && (
          <span className={`${styles.leyendaItem} ${styles.leyendaObservacion}`}>
            <span className={styles.puntoLeyenda} />
            Con observación
          </span>
        )}

        {/* Los códigos, plegables, a la derecha.

            Plegadas, la fila queda con las solapas y un botón. Desplegadas, los códigos
            aparecen a la derecha del botón y scrollean si no entran. El alto de la fila no
            cambia entre un estado y el otro: la grilla no se mueve al abrirlas. */}
        <div className={styles.referencias}>
          {/* El botón ARRIBA del panel y pegado a la izquierda, como el título que era
              antes. El bloque se alinea a la derecha, así que plegado queda contra el borde
              y al abrirse crece hacia la izquierda llevándose el botón con él.

              Los códigos se renderizan SIEMPRE, abiertos o no: para animar la apertura el
              elemento tiene que existir antes y después. Plegados quedan en ancho cero con
              el overflow oculto. */}
          <button
            type="button"
            className={styles.referenciasBoton}
            onClick={() => setReferenciasAbiertas((abiertas) => !abiertas)}
            aria-expanded={referenciasAbiertas}
          >
            Referencias
            <ChevronLeft
              fontSize="small"
              className={referenciasAbiertas ? styles.chevronAbierto : styles.chevron}
            />
          </button>

          <div
            className={`${styles.referenciasItems} ${
              referenciasAbiertas ? styles.referenciasItemsAbierto : ""
            }`}
            aria-hidden={!referenciasAbiertas}
          >
            {TURNOS.map((t) => (
              <span key={t.codigo} className={styles.leyendaItem}>
                <span className={styles.turnoMini}>{t.codigo}</span>
                {t.horario ? `${t.label} (${t.horario})` : t.label}
              </span>
            ))}
            {/* Solo si el mes tiene alguno, igual que el punto de observacion: una
                referencia para algo que no esta en pantalla es ruido.

                El cuadradito va VACIO y no con una letra: el feriado no es un codigo que
                se elija en la celda, es el color de la columna entera. */}
            {hayFeriados && (
              <>
                <span className={styles.leyendaItem}>
                  <span className={`${styles.turnoMini} ${styles.feriadoNacional}`} />
                  Feriado nacional
                </span>
                <span className={styles.leyendaItem}>
                  <span className={`${styles.turnoMini} ${styles.feriadoProvincial}`} />
                  Feriado provincial
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.gridWrapper}>
        <DataGridPro
          className={`${gridClasses.root} ${styles.horariosGrid}`}
          columns={enTurnos ? columns : columnsTEx}
          rows={enTurnos ? rows : tex.rows}
          // Sin autoHeight: la grilla toma el alto del wrapper (flex) y el scroll
          // vertical queda adentro; el header queda fijo arriba y los totales abajo.
          hideFooter
          loading={isLoading}
          editMode="cell"
          // newEditingApi: en v5, processRowUpdate/renderEditCell lo requieren.
          // rowPinning: fija la fila de totales al fondo (ver pinnedRows).
          experimentalFeatures={{ newEditingApi: true, rowPinning: true }}
          // VISTA2 no tiene totales que mostrar: sin datos, la fila daria todo en cero.
          pinnedRows={enTurnos ? pinnedRows : undefined}
          disableColumnResize
          disableColumnReorder
          // COPIAR (desconectado): los checkboxes solo servian para elegir filas a copiar.
          //   checkboxSelection
          //   selectionModel={filasTildadas}
          //   onSelectionModelChange={setFilasTildadas}
          disableSelectionOnClick
          // CONTROLADA y no `initialState`: el cierre de cada solapa tiene nombre propio
          // —`francos` en Turnos, `guardias` en TEx— y el initialState solo se aplica al
          // montar, asi que al cambiar de solapa la columna quedaria sin pinear.
          //
          // COPIAR (desconectado): al volver los checkboxes, su columna va primera en `left`
          // con GRID_CHECKBOX_SELECTION_COL_DEF.field.
          pinnedColumns={{
            left: ["empleado"],
            right: [enTurnos ? "francos" : "guardias"],
          }}
          isCellEditable={(params) => params.row.id !== TOTAL_ROW_ID}
          processRowUpdate={enTurnos ? processRowUpdate : tex.processRowUpdate}
          onProcessRowUpdateError={(error) => console.error("Error al actualizar fila:", error)}
          // La seleccion de celda habilita la lupa de detalle, que solo aplica a turnos.
          onCellClick={enTurnos ? handleCellClick : undefined}
          getCellClassName={(params) => {
            // La fila de totales no se tiñe de feriado: `.filaTotales` lleva
            // `!important` y le gana igual. Ver la nota en el scss.
            if (params.id === TOTAL_ROW_ID) return styles.filaTotales;
            const clases = [];
            // Primero el feriado: es lo más de fondo de todo, y las clases que vienen
            // después tienen que poder taparlo.
            const feriado = marcaDeFeriado(feriados, params.field);
            if (feriado) clases.push(feriado.clase);
            // SIN COLOR: la celda va en blanco con la letra negra. Para volver a teñirla
            // por código, descomentar — los tintes siguen definidos en `_turnos.scss` y los
            // usa la leyenda:
            //   const codigo = params.value?.codigo;
            //   if (codigo) clases.push(styles[`celdaTurno${codigo}`]);
            if (cambiosPendientes[`${params.id}-${params.field}`]) clases.push(styles.celdaModificada);
            if (celdaSeleccionada && celdaSeleccionada.id === params.id && celdaSeleccionada.field === params.field) {
              clases.push(styles.celdaSeleccionada);
            }
            return clases.join(" ");
          }}
          rowHeight={ALTO_FILA}
          hideFooterSelectedRowCount
          disableColumnMenu
          localeText={{ ...esES.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: "Sin datos" }}
        />
      </div>
    </main>
  );
}
